import { createFileRoute, Link } from '@tanstack/react-router';
import { RefreshCw, UserPlus, Mail, Phone } from '@/lib/icons';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { useIsMobile } from '@/hooks/use-mobile';

import { useAppSelector } from '@/store/hooks';
import { getAllUsers, userQueryKeys } from '@/features/users/api';
import type { UserListItem } from '@/features/users/user';
import { hasPermission } from '@/lib/auth/permissions';
import { ListPageShell } from '@/components/layout/ListPageShell';
import { SearchField } from '@/components/filters';
import { useListFilters } from '@/hooks/use-list-filters';
import { ButtonSpinner, SectionLoading } from '@/components/feedback';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import {
  ResponsiveTable,
  createMobileCards,
  MobileCardField,
} from '@/components/ui/MobileCard';
import {
  getRoleTemplates,
  roleDisplayName,
  roleTemplateQueryKeys,
} from '@/features/users/permission-templates';
import { formatDate } from '@/lib/dates';

const getUserStatus = (user: UserListItem) =>
  user.isActive ? 'active' : 'inactive';

const usersSearchSchema = z.object({
  q: z.string().optional(),
  role: z.string().optional(),
  status: z.enum(['active', 'inactive']).optional(),
});

export const Route = createFileRoute('/_layout/users/')({
  validateSearch: usersSearchSchema,
  component: UsersListPage,
});

function UsersListPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const search = Route.useSearch();

  const {
    searchInput,
    setSearchInput,
    debouncedSearch,
    isSearchPending,
    setFilters,
  } = useListFilters({ search, to: '/users' });

  const roleFilter = search.role ?? 'all';
  const statusFilter = search.status ?? 'all';

  const currentUser = useAppSelector((state) => state.auth.user);

  const isAuthorized = hasPermission(currentUser, 'users', 'read');

  const {
    data: usersResult,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: userQueryKeys.list(debouncedSearch),
    queryFn: () => getAllUsers({ search: debouncedSearch }),
    enabled: isAuthorized,
    placeholderData: (previous) => previous,
  });

  const users = usersResult?.value ?? [];

  const { data: templatesResult } = useQuery({
    queryKey: roleTemplateQueryKeys.all,
    queryFn: getRoleTemplates,
    enabled: isAuthorized,
    staleTime: Infinity,
  });
  const templates = templatesResult?.value ?? [];

  // Administrators are named by what they are; everyone else by the template
  // they were given, or "Custom" once their matrix has been edited away from
  // every one of them — so an over-permissioned account is visible in the list
  // instead of hiding behind a familiar label.
  const roleLabel = (u: (typeof users)[number]): string =>
    roleDisplayName(u, templates, {
      superAdmin: t('users.roleLabel.superAdmin'),
      tenantAdmin: t('users.roleLabel.tenantAdmin'),
      custom: t('users.permissions.template.custom'),
    });

  const roleNames = Array.from(new Set(users.map(roleLabel))).sort();

  // Search is server-side. Role and status stay local because neither exists as
  // a column: role is derived from the permission matrix at render time, and
  // the users endpoint takes no active flag.
  const filteredUsers = users.filter((user) => {
    const matchesRole = roleFilter === 'all' || roleLabel(user) === roleFilter;
    const matchesStatus =
      statusFilter === 'all' || getUserStatus(user) === statusFilter;
    return matchesRole && matchesStatus;
  });

  const handleRefresh = () => {
    void refetch();
  };

  if (!isAuthorized) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <h1 className="text-2xl font-bold mb-4">
          {t('users.list.accessDenied')}
        </h1>
        <p className="text-muted-foreground">{t('users.list.noPermission')}</p>
      </div>
    );
  }

  return (
    <ListPageShell
      title={t('common.users')}
      actions={
        <>
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            title={t('common.refreshList')}
            disabled={loading}
          >
            {loading ? <ButtonSpinner /> : <RefreshCw className="h-4 w-4" />}
          </Button>
          {hasPermission(currentUser, 'users', 'create') && (
            <Button asChild size={isMobile ? 'icon' : 'default'}>
              <Link to="/users/new">
                <UserPlus className={isMobile ? 'h-4 w-4' : 'mr-1 h-4 w-4'} />
                {!isMobile && <span>{t('users.list.addUser')}</span>}
              </Link>
            </Button>
          )}
        </>
      }
      filters={
        <>
          <SearchField
            value={searchInput}
            onChange={setSearchInput}
            placeholder={t('filters.searchPlaceholder.users')}
            isPending={isSearchPending}
          />

          <div className="flex-shrink-0">
            <Select
              value={roleFilter}
              onValueChange={(role) =>
                setFilters({ role: role === 'all' ? undefined : role })
              }
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t('users.list.roleFilter')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('users.list.allRoles')}</SelectItem>
                {roleNames.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex-shrink-0">
            <Select
              value={statusFilter}
              onValueChange={(status) =>
                setFilters({ status: status === 'all' ? undefined : status })
              }
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder={t('common.status.title')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.allStatuses')}</SelectItem>
                <SelectItem value="active">
                  {t('common.status.active')}
                </SelectItem>
                <SelectItem value="inactive">
                  {t('common.status.inactive')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      }
      hasData={!loading && filteredUsers.length > 0}
      emptyState={loading ? <SectionLoading /> : null}
    >
      <ResponsiveTable
        fallbackMessage={t('users.list.noUsersFound')}
        mobileCards={
          filteredUsers.length > 0
            ? createMobileCards(filteredUsers, {
                keyExtractor: (user) => user.id,
                getTitle: (user) => `${user.firstName} ${user.lastName}`,
                getSubtitle: (user) => {
                  if (user.email) {
                    return (
                      <div className="flex items-center">
                        <Mail className="h-3.5 w-3.5 mr-1" />
                        <span>{user.email}</span>
                      </div>
                    );
                  }
                  if (user.phoneNumber) {
                    return (
                      <div className="flex items-center">
                        <Phone className="h-3.5 w-3.5 mr-1" />
                        <span>{user.phoneNumber}</span>
                      </div>
                    );
                  }
                  return '-';
                },
                getFields: (user): MobileCardField[] => [
                  {
                    key: 'role',
                    label: t('common.role'),
                    value: roleLabel(user),
                    type: 'badge',
                    variant: 'outline',
                    priority: 'high',
                  },
                  {
                    key: 'status',
                    label: t('common.status.title'),
                    value: user.isActive
                      ? t('common.status.active')
                      : t('common.status.inactive'),
                    type: 'badge',
                    variant: user.isActive ? 'default' : 'secondary',
                    priority: 'high',
                  },
                  {
                    key: 'email',
                    label: t('common.form.email'),
                    value: user.email ? (
                      <div className="flex items-center">
                        <Mail className="h-3.5 w-3.5 mr-1 text-muted-foreground" />
                        <span>{user.email}</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    ),
                    priority: 'medium',
                  },
                  {
                    key: 'phone',
                    label: t('common.form.phoneNumber'),
                    value: user.phoneNumber ? (
                      <div className="flex items-center">
                        <Phone className="h-3.5 w-3.5 mr-1 text-muted-foreground" />
                        <span>{user.phoneNumber}</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    ),
                    priority: 'medium',
                  },
                  {
                    key: 'createdAt',
                    label: t('common.createdAt'),
                    value: formatDate(user.createdAt),
                    priority: 'low',
                  },
                ],
                getActions: (user) => (
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/users/$userId" params={{ userId: user.id }}>
                      {t('common.details')}
                    </Link>
                  </Button>
                ),
              })
            : undefined
        }
      >
        <div className="rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('common.name')}</TableHead>
                <TableHead>{t('common.form.email')}</TableHead>
                <TableHead>{t('common.form.phoneNumber')}</TableHead>
                <TableHead>{t('common.role')}</TableHead>
                <TableHead>{t('common.status.title')}</TableHead>
                <TableHead className="text-right">
                  {t('common.actionsTitle')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center py-8 text-muted-foreground"
                  >
                    {t('users.list.noUsersFound')}
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">
                      {user.firstName} {user.lastName}
                    </TableCell>
                    <TableCell>{user.email || '-'}</TableCell>
                    <TableCell>{user.phoneNumber || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {roleLabel(user)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={user.isActive ? 'default' : 'secondary'}
                        className="capitalize"
                      >
                        {user.isActive
                          ? t('common.status.active')
                          : t('common.status.inactive')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" asChild>
                        <Link to="/users/$userId" params={{ userId: user.id }}>
                          {t('common.details')}
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </ResponsiveTable>
    </ListPageShell>
  );
}
